import React, { useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import HPButton from "@components/common/Button";
import HPDropdown from "@components/common/Dropdown";
import {
  useGetDatasetQuestionsQuery,
  useGetDatasetQuestionVariablesByElementQuery,
} from "@store/api/dataset-api";
import type { VisualizationBaseCondition } from "@models/VisualizationBase";
import {
  BASE_QUESTION_SHAPE_LABEL,
  classifyBaseQuestionShape,
  getValueLabel,
  getVariableLabel,
  isBaseConditionValid,
} from "@utils/visualization-base-utils";
import { useBaseQuestionSearch } from "@hooks/use-base-question-search";
import { buildHiddenReasonMap } from "@utils/chart-visibility-utils";
import { t } from "@utils/i18n";

interface BaseConditionBuilderProps {
  datasetId: string;
  condition: VisualizationBaseCondition;
  excludedQuestionIds: string[];
  isDisabled?: boolean;
  isEditing: boolean;
  onChange: (updated: VisualizationBaseCondition) => void;
  onCancel: () => void;
  onSave: () => void;
}

const BaseConditionBuilder: React.FC<BaseConditionBuilderProps> = ({
  datasetId,
  condition,
  excludedQuestionIds,
  isDisabled,
  isEditing,
  onChange,
  onCancel,
  onSave,
}) => {
  const {
    questions: searchedQuestions,
    searchTerm,
    setSearchTerm,
    hasMore,
    isFetching,
    observerTarget,
  } = useBaseQuestionSearch(datasetId);

  // Resolves the selected question's current label independent of the search
  // results, so hydrated conditions always display correctly even if the
  // question isn't on the currently-searched page.
  const { data: selectedQuestionData } = useGetDatasetQuestionsQuery(
    { id: datasetId, dataset_element_ids: [condition.questionId], page_size: 1 },
    { skip: !datasetId || !condition.questionId },
  );

  const { data: questionVariablesData } =
    useGetDatasetQuestionVariablesByElementQuery(
      { id: datasetId, dataset_element_id: condition.questionId },
      { skip: !datasetId || !condition.questionId },
    );

  const variables = useMemo(
    () => questionVariablesData?.variables ?? [],
    [questionVariablesData],
  );
  const values = useMemo(
    () => questionVariablesData?.values ?? [],
    [questionVariablesData],
  );

  const shape = classifyBaseQuestionShape(condition.questionType, variables.length);
  const isValid = isBaseConditionValid(condition, shape);

  const mergedQuestions = useMemo(() => {
    const merged = [...searchedQuestions];
    const selected = selectedQuestionData?.questions?.[0];
    if (selected && !merged.some((q) => q.datasetElementId === selected.datasetElementId)) {
      merged.unshift(selected);
    }
    return merged;
  }, [searchedQuestions, selectedQuestionData]);

  const questionOptions = useMemo(() => {
    const excluded = new Set(excludedQuestionIds);
    return mergedQuestions
      .filter((q) => !excluded.has(q.datasetElementId))
      .map((q) => {
        const optionShape = classifyBaseQuestionShape(q.type, q.variablesCount);
        const shapeLabel = optionShape
          ? BASE_QUESTION_SHAPE_LABEL[optionShape]
          : undefined;
        return {
          value: q.datasetElementId,
          label: q.name || q.title,
          subtitle: q.hidden
            ? t("common.hiddenInDataset")
            : shapeLabel,
        };
      });
  }, [mergedQuestions, excludedQuestionIds]);

  const handleQuestionChange = (questionId: string) => {
    const selected = mergedQuestions.find(
      (q) => q.datasetElementId === questionId,
    );
    onChange({
      id: condition.id,
      questionId,
      questionType: selected?.type === "multi" ? "multi" : "single",
      variableIds: [],
      valueIds: [],
    });
  };

  // Members already saved in this condition stay selected even if hidden —
  // only new/replacement picks are restricted to visible sources.
  const hiddenVariableReasons = useMemo(
    () => buildHiddenReasonMap(variables, (v) => v.datasetElementId),
    [variables],
  );
  const hiddenValueReasons = useMemo(
    () => buildHiddenReasonMap(values, (v) => v.datasetElementId),
    [values],
  );

  const variableOptions = useMemo(
    () =>
      variables.map((v) => ({
        value: v.datasetElementId,
        label: getVariableLabel(v),
        subtitle: hiddenVariableReasons.get(v.datasetElementId),
      })),
    [variables, hiddenVariableReasons],
  );
  const valueOptions = useMemo(
    () =>
      values.map((v) => ({
        value: v.datasetElementId,
        label: getValueLabel(v),
        subtitle: hiddenValueReasons.get(v.datasetElementId),
      })),
    [values, hiddenValueReasons],
  );

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <HPDropdown
        label="Question"
        value={condition.questionId}
        onChange={handleQuestionChange}
        options={questionOptions}
        placeholder="Search for a question"
        disabled={isDisabled}
        onInputChange={(_, v) => setSearchTerm(v)}
        hasMore={hasMore}
        loading={isFetching}
        observerTarget={observerTarget}
      />

      {questionOptions.length === 0 && !isFetching && !searchTerm && (
        <Alert severity="info">
          No visible questions are available for a base condition.{" "}
          {datasetId && (
            <a href={`/datasets/${datasetId}?tab=questions`}>
              Open Questions
            </a>
          )}{" "}
          to restore content.
        </Alert>
      )}

      {condition.questionId && shape === "single" && (
        <>
          {valueOptions.length === 0 ? (
            <Alert severity="info">
              No visible values are available for this question.{" "}
              {datasetId && (
                <a href={`/datasets/${datasetId}?tab=questions`}>
                  Open Questions
                </a>
              )}{" "}
              to restore content.
            </Alert>
          ) : (
            <>
              <HPDropdown
                label="Values"
                multiSelect
                value={condition.valueIds}
                onChange={(ids) => onChange({ ...condition, valueIds: ids })}
                options={valueOptions}
                placeholder="Select one or more values"
                disabled={isDisabled}
                countLabel="Value"
                getOptionDisabled={(option) =>
                  hiddenValueReasons.has(option.value) &&
                  !condition.valueIds.includes(option.value)
                }
              />
              <Typography variant="caption" color="text.secondary">
                Any selected option
              </Typography>
            </>
          )}
        </>
      )}

      {condition.questionId && shape === "multiple" && (
        <>
          {variableOptions.length === 0 ? (
            <Alert severity="info">
              No visible variables are available for this question.{" "}
              {datasetId && (
                <a href={`/datasets/${datasetId}?tab=questions`}>
                  Open Questions
                </a>
              )}{" "}
              to restore content.
            </Alert>
          ) : (
            <>
              <HPDropdown
                label="Variables"
                multiSelect
                value={condition.variableIds}
                onChange={(ids) =>
                  onChange({ ...condition, variableIds: ids })
                }
                options={variableOptions}
                placeholder="Select one or more variables"
                disabled={isDisabled}
                countLabel="Variable"
                searchable={false}
                getOptionDisabled={(option) =>
                  hiddenVariableReasons.has(option.value) &&
                  !condition.variableIds.includes(option.value)
                }
              />
              <Typography variant="caption" color="text.secondary">
                Any selected option
              </Typography>
            </>
          )}
        </>
      )}

      {condition.questionId && shape === "grid" && (
        <>
          {variableOptions.length === 0 || valueOptions.length === 0 ? (
            <Alert severity="info">
              No visible {variableOptions.length === 0 ? "variables" : "values"}{" "}
              are available for this question.{" "}
              {datasetId && (
                <a href={`/datasets/${datasetId}?tab=questions`}>
                  Open Questions
                </a>
              )}{" "}
              to restore content.
            </Alert>
          ) : (
            <>
              <HPDropdown
                label="Variables"
                multiSelect
                value={condition.variableIds}
                onChange={(ids) =>
                  onChange({ ...condition, variableIds: ids })
                }
                options={variableOptions}
                placeholder="Select one or more variables"
                disabled={isDisabled}
                countLabel="Variable"
                searchable={false}
                getOptionDisabled={(option) =>
                  hiddenVariableReasons.has(option.value) &&
                  !condition.variableIds.includes(option.value)
                }
              />
              <HPDropdown
                label="Values"
                multiSelect
                value={condition.valueIds}
                onChange={(ids) => onChange({ ...condition, valueIds: ids })}
                options={valueOptions}
                placeholder="Select one or more values"
                disabled={isDisabled}
                countLabel="Value"
                getOptionDisabled={(option) =>
                  hiddenValueReasons.has(option.value) &&
                  !condition.valueIds.includes(option.value)
                }
              />
              <Typography variant="caption" color="text.secondary">
                Any selected variable matching any selected value
              </Typography>
            </>
          )}
        </>
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <HPButton variant="outlined" size="small" onClick={onCancel}>
          Cancel
        </HPButton>
        <HPButton
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={!isValid || isDisabled}
        >
          {isEditing ? "Save" : "Add"}
        </HPButton>
      </Box>
    </Box>
  );
};

export default BaseConditionBuilder;
